import { useState } from 'react';
import {
  CourseLevel,
  CourseLevelLog,
  SuccessResponse,
} from '../types/CourseStageAccordion.type';
import { apiGet, apiPost } from '../utils/apiClient';
import { notifyAPIClient, notifyFailure } from '../utils/toast';
import { Dayjs } from 'dayjs';

export const useCourseStageAccordionController = () => {
  const [currentLevel, setCurrentLevel] = useState<string>('');
  const [currentLevelNumber, setCurrentLevelNumber] = useState<number>(0);
  const [courseLevels, setCourseLevels] = useState<CourseLevel[]>([]);
  const [selectedCourseLevel, setSelectedCourseLevel] = useState(0);

  // Course Level Log
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [courseLevelLogData, setCourseLevelLogData] = useState<
    CourseLevelLog[]
  >([]);
  const [filterDate, setFilterDate] = useState<Dayjs | null>(null);

  const [openEntryDialog, setOpenEntryDialog] = useState(false);
  const [openUncheckStageDialog, setOpenUncheckStageDialog] = useState(false);

  // Highlighted row
  const [highlightedRow, setHighlightedRow] = useState<string | null>(null);

  // Course Level Log Fetching
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const fetchCourseLevels = async (subSubTypeId: number) => {
    try {
      const res = await apiGet<SuccessResponse>(
        `/course/get-levels/${subSubTypeId}`,
        {},
      );
      setCourseLevels(res.data);
    } catch (error) {
      console.error('Failed to fetch course levels:', error);
    }
  };

  const handleSetEntryLevel = async (
    subSubTypeId: number,
    registrationId: number,
  ): Promise<boolean> => {
    try {
      const res = await apiPost<
        SuccessResponse,
        {
          level: number;
        }
      >(`/course/set-entry-level/${subSubTypeId}/${registrationId}`, {
        level: selectedCourseLevel,
      });
      notifyAPIClient(res.message);
      return true;
    } catch (error) {
      console.error('Failed to set entry level:', error);
      notifyFailure();
      return false;
    }
  };

  const fetchLevelLogs = async (registrationId: number) => {
    try {
      const res = await apiGet<SuccessResponse>(
        `/course/get-course-level-logs/${registrationId}?page=${page + 1}&limit=${limit}&date=${
          filterDate && filterDate.isValid()
            ? filterDate.format('YYYY-MM-DD')
            : ''
        }`,
        {},
      );

      setCourseLevelLogData(res.data);
      setTotalItems(res.metadata.totalItems);
    } catch (error) {
      console.error('Failed to fetch level logs:', error);
    }
  };

  const handleCurrentLevel = async (
    subSubTypeId: number,
    registrationId: number,
  ) => {
    try {
      const res = await apiGet<SuccessResponse>(
        `/course/get-current-level/${subSubTypeId}/${registrationId}`,
        {},
      );
      setCurrentLevel(res.data.levelName as string);
      setCurrentLevelNumber(res.data.level as number);
      setSelectedCourseLevel(res.data.level as number);
    } catch (error) {
      console.error('Failed to get current level:', error);
    }
  };

  const handleCompleteStage = async (
    registrationId: number,
    courseStageId: number,
  ) => {
    try {
      const res = await apiPost<SuccessResponse, Record<string, never>>(
        `/course/complete-stage/${registrationId}/${courseStageId}`,
        {},
      );
      notifyAPIClient(res.message);
    } catch (error) {
      console.error('Failed to complete stage:', error);
      notifyFailure();
    }
  };

  const calculateAge = (dob: string | Date) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Entry Level Dialog Handlers
  const handleEntryDialogOpen = () => setOpenEntryDialog(true);
  const handleEntryDialogClose = () => setOpenEntryDialog(false);

  // Entry Level Dialog Handlers
  const handleUncheckStageDialogOpen = () => setOpenUncheckStageDialog(true);
  const handleUncheckStageDialogClose = () => setOpenUncheckStageDialog(false);

  return {
    currentLevel,
    currentLevelNumber,
    handleCurrentLevel,
    handleCompleteStage,
    calculateAge,
    courseLevels,
    fetchCourseLevels,
    selectedCourseLevel,
    setSelectedCourseLevel,
    handleSetEntryLevel,

    limit,
    page,
    totalItems,
    courseLevelLogData,
    fetchLevelLogs,
    handleChangePage,
    handleChangeRowsPerPage,
    filterDate,
    setFilterDate,

    openEntryDialog,
    handleEntryDialogOpen,
    handleEntryDialogClose,

    openUncheckStageDialog,
    handleUncheckStageDialogOpen,
    handleUncheckStageDialogClose,

    highlightedRow,
    setHighlightedRow,
  };
};
